import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Archive, ArrowRight, Clock, FileText, Inbox, Newspaper, Trash2 } from 'lucide-react';
import type { PostListItem, PostStatus } from '@news-cms/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FailedLoginsCard } from '@/components/dashboard/failed-logins-card';
import { PendingCommentsCard } from '@/components/dashboard/pending-comments-card';
import { PendingReviewCard } from '@/components/dashboard/pending-review-card';
import { RecentUsersCard } from '@/components/dashboard/recent-users-card';
import { RecentsWidget } from '@/components/dashboard/recents-widget';
import { SecurityStatsCard } from '@/components/dashboard/security-stats-card';
import { TopActionsCard } from '@/components/dashboard/top-actions-card';
import { TopViewedCard } from '@/components/dashboard/top-viewed-card';
import { WebhookHealthCard } from '@/components/dashboard/webhook-health-card';
import { useSession } from '@/lib/auth';
import { hasPermission } from '@/lib/permissions';
import { roleLabelTh } from '@/lib/role-label';
import { auditListQueryOptions } from '@/lib/audit';
import { postsListQueryOptions } from '@/lib/posts';

export const Route = createFileRoute('/_authenticated/')({
  component: DashboardPage,
});

function DashboardPage() {
  const { user } = useSession();
  if (!user) return null;

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
            สวัสดี, {user.name}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            @{user.username} · {roleLabelTh(user.role.slug)}{' '}
            <span className="font-mono text-xs">({user.role.slug})</span>
          </p>
        </div>

        <StatsRow />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <RecentPostsWidget />
          <RecentsWidget />
          <TopViewedCard />
          {hasPermission(user, 'post.create') && <MyDraftsWidget userId={user.id} />}
          {hasPermission(user, 'post.create') && <MyStaleDraftsWidget userId={user.id} />}
          {hasPermission(user, 'post.publish') && <PendingReviewCard />}
          {hasPermission(user, 'comment.moderate') && <PendingCommentsCard />}
          {hasPermission(user, 'audit.read') && <SecurityStatsCard />}
          {hasPermission(user, 'audit.read') && <TopActionsCard />}
          {hasPermission(user, 'audit.read') && <FailedLoginsCard />}
          {hasPermission(user, 'user.update') && <RecentUsersCard />}
          {hasPermission(user, 'webhook.manage') && <WebhookHealthCard />}
        </div>

        {hasPermission(user, 'audit.read') && <RecentAuditWidget />}
      </div>
    </div>
  );
}

/* ---------- Stats row ---------- */

function StatsRow() {
  const draftQ = useQuery(postsListQueryOptions({ status: ['draft'], perPage: 1 }));
  const pendingQ = useQuery(postsListQueryOptions({ status: ['pending_review'], perPage: 1 }));
  const scheduledQ = useQuery(postsListQueryOptions({ status: ['scheduled'], perPage: 1 }));
  const publishedQ = useQuery(postsListQueryOptions({ status: ['published'], perPage: 1 }));
  const archivedQ = useQuery(postsListQueryOptions({ status: ['archived'], perPage: 1 }));
  const trashQ = useQuery(postsListQueryOptions({ trash: true, perPage: 1 }));

  const pendingCount = pendingQ.data?.pagination.total ?? 0;
  const trashCount = trashQ.data?.pagination.total ?? 0;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      <StatCard
        label="ฉบับร่าง"
        value={draftQ.data?.pagination.total ?? '—'}
        icon={FileText}
        to="/posts"
        search={{ status: 'draft' }}
        tint="slate"
      />
      {/* Pending-review shifts to amber when non-zero — it's an action queue
          (publishers need to approve), not an inert count. Stays slate at 0. */}
      <StatCard
        label="รออนุมัติ"
        value={pendingQ.data?.pagination.total ?? '—'}
        icon={Inbox}
        to="/posts"
        search={{ status: 'pending_review' }}
        tint={pendingCount > 0 ? 'amber' : 'slate'}
      />
      <StatCard
        label="ตั้งเวลา"
        value={scheduledQ.data?.pagination.total ?? '—'}
        icon={Clock}
        to="/posts"
        search={{ status: 'scheduled' }}
        tint="amber"
      />
      <StatCard
        label="เผยแพร่"
        value={publishedQ.data?.pagination.total ?? '—'}
        icon={Newspaper}
        to="/posts"
        search={{ status: 'published' }}
        tint="emerald"
      />
      {/* Archived sits between published + trash conceptually — kept,
          not in default lists, recoverable via /unarchive. Stays slate
          to read as low-priority background state rather than action. */}
      <StatCard
        label="เก็บถาวร"
        value={archivedQ.data?.pagination.total ?? '—'}
        icon={Archive}
        to="/posts"
        search={{ status: 'archived' }}
        tint="slate"
      />
      {/* Trash is recovery surface — always render so admin gets a reminder
          to clear it, but stays slate (low prominence) regardless of count. */}
      <StatCard
        label="ถังขยะ"
        value={trashCount}
        icon={Trash2}
        to="/posts"
        search={{ trash: true }}
        tint="slate"
      />
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: number | string;
  icon: typeof FileText;
  to: '/posts';
  // The posts validateSearch reads `trash` as boolean true (not "true"
  // string), so search values need both shapes — status pills pass
  // strings, the trash card passes a real boolean.
  search?: Record<string, string | boolean>;
  tint: 'slate' | 'amber' | 'emerald';
}

function StatCard({ label, value, icon: Icon, to, search, tint }: StatCardProps) {
  const tintClasses: Record<StatCardProps['tint'], string> = {
    slate: 'text-slate-500',
    amber: 'text-amber-500',
    emerald: 'text-emerald-500',
  };
  return (
    <Link
      to={to}
      search={search}
      className="group rounded-lg border border-slate-200 bg-white p-4 transition hover:border-slate-300 hover:shadow-sm dark:border-slate-800 dark:bg-slate-900"
    >
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">{label}</div>
        <Icon className={`h-4 w-4 ${tintClasses[tint]}`} />
      </div>
      <div className="mt-2 text-2xl font-bold text-slate-900 dark:text-slate-50">{value}</div>
      <div className="mt-1 flex items-center text-[11px] text-slate-400 group-hover:text-slate-600">
        ดูทั้งหมด <ArrowRight className="ml-1 h-3 w-3" />
      </div>
    </Link>
  );
}

/* ---------- Recent posts ---------- */

function RecentPostsWidget() {
  const query = useQuery(
    postsListQueryOptions({ perPage: 5, sortBy: 'updatedAt', sortDir: 'desc' }),
  );
  const items = query.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">บทความล่าสุด</CardTitle>
        <Link to="/posts" className="text-xs text-blue-600 hover:underline dark:text-blue-400">
          ดูทั้งหมด →
        </Link>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <p className="text-sm text-slate-500">กำลังโหลด...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-400">ยังไม่มีบทความ</p>
        ) : (
          <ul className="space-y-2">
            {items.map((p) => (
              <PostRow key={p.id} item={p} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/* ---------- My drafts ----------
 *
 * Mirrors RecentPostsWidget but scoped to the current user's drafts +
 * pending-review submissions. The realistic "pick up where I left off"
 * use case for authors logging back in. "ดูทั้งหมด" deep-links into
 * /posts?authorId=<me>&status=draft so the filtered list opens
 * pre-narrowed. */
function MyDraftsWidget({ userId }: { userId: string }) {
  const query = useQuery(
    postsListQueryOptions({
      authorId: userId,
      status: ['draft', 'pending_review'],
      perPage: 5,
      sortBy: 'updatedAt',
      sortDir: 'desc',
    }),
  );
  const items = query.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">ฉบับร่างของฉัน</CardTitle>
        <Link
          to="/posts"
          search={{ authorId: userId, status: 'draft' }}
          className="text-xs text-blue-600 hover:underline dark:text-blue-400"
        >
          ดูทั้งหมด →
        </Link>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <p className="text-sm text-slate-500">กำลังโหลด...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-400">ไม่มีฉบับร่างค้างอยู่ — เริ่มเขียนใหม่ได้เลย</p>
        ) : (
          <ul className="space-y-2">
            {items.map((p) => (
              <PostRow key={p.id} item={p} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/* ---------- My stale drafts ----------
 *
 * Drafts the current user authored that haven't been touched in 14+
 * days. Pairs with the "ค้าง Nd" chip in the /posts list — surfaces
 * the same forgotten-work signal on the dashboard where editors land
 * first.
 *
 * Renders nothing when there are no stale drafts (silence is the
 * right signal); otherwise shows up to 5 oldest-first so the most
 * neglected work bubbles up. Fetches author's drafts oldest-first
 * via the existing list endpoint and client-filters by date — avoids
 * needing a new server-side `updatedBefore` filter for a single
 * cosmetic surface. */
const STALE_DRAFT_DAYS = 14;

function MyStaleDraftsWidget({ userId }: { userId: string }) {
  const query = useQuery(
    postsListQueryOptions({
      authorId: userId,
      status: ['draft'],
      perPage: 100,
      sortBy: 'updatedAt',
      sortDir: 'asc',
    }),
  );
  const cutoff = Date.now() - STALE_DRAFT_DAYS * 24 * 60 * 60 * 1000;
  const stale = (query.data?.items ?? [])
    .filter((p) => new Date(p.updatedAt).getTime() < cutoff)
    .slice(0, 5);

  // Quiet when there's nothing to clean up — editors don't need a
  // green "0 stale" card cluttering the dashboard.
  if (!query.isLoading && stale.length === 0) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="h-4 w-4 text-amber-500" />
          ฉบับร่างค้างของฉัน
        </CardTitle>
        <Link
          to="/posts"
          search={{ authorId: userId, status: 'draft' }}
          className="text-xs text-blue-600 hover:underline dark:text-blue-400"
        >
          ดูทั้งหมด →
        </Link>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <p className="text-sm text-slate-500">กำลังโหลด...</p>
        ) : (
          <ul className="space-y-2">
            {stale.map((p) => (
              <PostRow key={p.id} item={p} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function PostRow({ item }: { item: PostListItem }) {
  const statusLabel: Record<PostStatus, string> = {
    draft: 'ฉบับร่าง',
    pending_review: 'รออนุมัติ',
    scheduled: 'ตั้งเวลา',
    published: 'เผยแพร่',
    archived: 'เก็บถาวร',
  };
  return (
    <li className="flex items-baseline justify-between gap-3 border-b border-slate-100 pb-2 last:border-0 dark:border-slate-800">
      <Link
        to="/posts/$postId"
        params={{ postId: item.id }}
        className="hover:text-brand-600 dark:hover:text-brand-400 line-clamp-1 flex-1 text-sm font-medium text-slate-900 dark:text-slate-50"
      >
        {item.title}
      </Link>
      <span className="shrink-0 text-[11px] text-slate-500">{statusLabel[item.status]}</span>
      <span className="shrink-0 text-[11px] text-slate-400">
        {new Date(item.updatedAt).toLocaleDateString('th-TH')}
      </span>
    </li>
  );
}

/* ---------- Recent audit ---------- */

function RecentAuditWidget() {
  const query = useQuery(auditListQueryOptions({ perPage: 10 }));
  const items = query.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">กิจกรรมล่าสุด</CardTitle>
        <Link to="/audit" className="text-xs text-blue-600 hover:underline dark:text-blue-400">
          ดูทั้งหมด →
        </Link>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <p className="text-sm text-slate-500">กำลังโหลด...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-400">ยังไม่มีกิจกรรม</p>
        ) : (
          <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {items.map((a) => (
              <li key={a.id} className="flex items-baseline gap-3 py-2">
                <span className="shrink-0 font-mono text-[11px] text-slate-500">
                  {new Date(a.createdAt).toLocaleTimeString('th-TH', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
                <span className="shrink-0 text-xs text-slate-700 dark:text-slate-300">
                  {a.userName ?? 'anon'}
                </span>
                <span className="flex-1 truncate font-mono text-[11px] text-slate-500">
                  {a.action}
                </span>
                <span className="shrink-0 font-mono text-[10px] text-slate-400">
                  {a.resourceType}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
